import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Check, MapPin, Star, BedDouble, Users } from "lucide-react";
import { calculateAccommodationTotal, formatSGD } from "@/utils/pricingEngine";

/**
 * @param {{
 *   open: boolean,
 *   onOpenChange: (open: boolean) => void,
 *   options: any[],
 *   selectedId: string | undefined,
 *   nights: number,
 *   nightDates?: (Date | string)[], // one entry per night, in order — enables accurate weekend/holiday/additional-night pricing per option. Falls back to price × nights if omitted.
 *   sharingType?: string,
 *   numRooms?: number, // rooms the golfer is booking — totals are per room × rooms
 *   onSelect: (accommodation: any) => void,
 * }} props
 */
export default function AccommodationOptionsDialog({
  open,
  onOpenChange,
  options,
  selectedId,
  nights,
  nightDates,
  sharingType,
  numRooms = 1,
  onSelect,
}) {
  const AnyDialogContent = /** @type {any} */ (DialogContent);

  const totalForOption = (a) => {
    if (Array.isArray(nightDates) && nightDates.length) {
      return calculateAccommodationTotal({
        accommodation: a,
        nightDates,
        sharingType,
        numRooms,
      }).total;
    }
    return (a.price || 0) * nights * numRooms;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <AnyDialogContent className="sm:max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#0B2421] text-xl">
            Choose Your Accommodation
          </DialogTitle>
          <DialogDescription>
            {options.length} curated hotels for your {nights} night
            {nights > 1 ? "s" : ""} stay
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 pt-2">
          {options.map((a) => {
            const isSelected = a.id === selectedId;
            return (
              <div
                key={a.id}
                className={`flex flex-col rounded-2xl overflow-hidden border-2 bg-white transition-all ${
                  isSelected
                    ? "border-[#0B2421] shadow-lg"
                    : "border-gray-100 hover:border-gray-300 hover:shadow-md"
                }`}
              >
                <div className="relative h-36 shrink-0 overflow-hidden">
                  <img
                    src={a.image}
                    className="w-full h-full object-cover"
                    alt={a.title}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
                  {a.rating != null && (
                    <span className="absolute top-3 left-3 bg-white/95 text-[#0B2421] text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1 shadow-sm">
                      <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                      {a.rating}
                    </span>
                  )}
                  {isSelected && (
                    <div className="absolute top-3 right-3 w-7 h-7 bg-[#D4AF37] rounded-full flex items-center justify-center shadow-sm">
                      <Check className="h-4 w-4 text-[#0B2421]" />
                    </div>
                  )}
                  <div className="absolute bottom-3 left-4">
                    <p className="text-white/70 text-[10px] uppercase tracking-wide">
                      From
                    </p>
                    <p className="text-white font-bold text-xl leading-none">
                      {formatSGD(a.price)}
                      <span className="text-xs font-normal text-white/70">
                        {" "}per room per night
                      </span>
                    </p>
                  </div>
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <h3 className="font-bold text-[#0B2421] text-base leading-snug">
                    {a.title}
                  </h3>
                  <div className="flex items-center gap-3 text-gray-400 text-xs mt-1 mb-3 flex-wrap">
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3 shrink-0" />
                      {a.destination}
                    </span>
                    {a.roomType && (
                      <span className="flex items-center gap-1">
                        <BedDouble className="h-3 w-3 shrink-0" />
                        {a.roomType}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-full px-2 py-0.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                      <Users className="h-3 w-3" />
                      Single Room · {a.bed_type || "King Bed"}
                    </span>
                  </div>
                  {a.amenities?.length > 0 && (
                    <ul className="space-y-1 mb-3">
                      {a.amenities.slice(0, 3).map((am) => (
                        <li
                          key={am}
                          className="flex items-center gap-1.5 text-xs text-gray-600"
                        >
                          <Check className="h-3 w-3 text-emerald-600 shrink-0" />
                          {am}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-auto">
                    <div className="flex items-center justify-between border-t border-gray-100 pt-2.5 mb-3">
                      <span className="text-xs text-gray-400">
                        Total for stay · {numRooms} room
                        {numRooms > 1 ? "s" : ""}
                      </span>
                      <span className="text-[#0B2421] font-bold text-sm">
                        {formatSGD(totalForOption(a))}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => onSelect(a)}
                      className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                        isSelected
                          ? "bg-[#D4AF37] text-[#0B2421]"
                          : "bg-[#0B2421] hover:bg-[#0B2421]/85 text-white"
                      }`}
                    >
                      {isSelected ? "Selected" : "Select"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </AnyDialogContent>
    </Dialog>
  );
}
